import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs';
import { ChatConfigService, DEFAULT_MESSAGE_MAX_CHARACTERS } from './core/chat/chat-config.service';
import { ChatHistoryService } from './core/chat/chat-history.service';
import { ChatMessageFormatPipe } from './core/chat/chat-message-format.pipe';
import { ChatSessionService } from './core/chat/chat-session.service';
import { ChatSocketService } from './core/chat/chat-socket.service';
import {
  ChatConnectionState,
  ChatErrorNotification,
  ChatLanguage,
  ChatSource,
  ChatSourcePayload,
  ReceiveMessagePayload,
  WidgetMessage,
} from './core/chat/chat.types';
import { environment } from '../environments/environment';

const MESSAGE_INPUT_MAX_HEIGHT = 150;
const MOBILE_MESSAGE_INPUT_MAX_HEIGHT = 120;
const MOBILE_VIEWPORT_MAX_WIDTH = 480;
const MESSAGE_SCROLL_TOP_OFFSET = 12;
const SUPPORTED_CHAT_LANGUAGES = new Set<ChatLanguage>(['es', 'en', 'it', 'fr', 'de']);
const PILGRIM_HOST_PATTERN = /(^|\.)pilgrim\.es$/i;
const PILGRIM_SOURCE_ICON_URL =
  'https://www.pilgrim.es/wp-content/uploads/2019/11/cropped-favicon-pilgrim-32x32.jpg';
const WIDGET_FRAME_MESSAGE_TYPE = 'ultreia-chat-widget:frame-state';
export const OFF_TOPIC_BLOCK_DURATION_MS = 24 * 60 * 60 * 1000;
const OFF_TOPIC_BLOCKED_MESSAGE =
  'Has alcanzado el límite de consultas fuera de tema. Podrás continuar más tarde con una nueva conversación sobre el Camino.';
const DEFAULT_CHAT_LANGUAGE: ChatLanguage = 'es';
const UI_TRANSLATIONS: Record<
  ChatLanguage,
  {
    inputPlaceholder: string;
    blockedInputPlaceholder: string;
    inputAriaLabel: string;
    minimizeChatLabel: string;
    resetChatLabel: string;
    sendMessageLabel: string;
    systemNoticeLabel: string;
    sourcesLabel: string;
    typingLabel: string;
  }
> = {
  es: {
    inputPlaceholder: 'Escribe aquí tu consulta',
    blockedInputPlaceholder: 'Podrás continuar más tarde',
    inputAriaLabel: 'Escribe tu consulta para UltreIA',
    minimizeChatLabel: 'Minimizar chat',
    resetChatLabel: 'Nueva conversación',
    sendMessageLabel: 'Enviar mensaje',
    systemNoticeLabel: 'Aviso',
    sourcesLabel: 'Fuentes',
    typingLabel: 'UltreIA está escribiendo',
  },
  en: {
    inputPlaceholder: 'Type your question here',
    blockedInputPlaceholder: 'You can continue later',
    inputAriaLabel: 'Type your question for UltreIA',
    minimizeChatLabel: 'Minimize chat',
    resetChatLabel: 'Restart',
    sendMessageLabel: 'Send message',
    systemNoticeLabel: 'Notice',
    sourcesLabel: 'Sources',
    typingLabel: 'UltreIA is typing',
  },
  it: {
    inputPlaceholder: 'Scrivi qui la tua domanda',
    blockedInputPlaceholder: 'Potrai continuare più tardi',
    inputAriaLabel: 'Scrivi la tua domanda per UltreIA',
    minimizeChatLabel: 'Riduci chat',
    resetChatLabel: 'Riavvia',
    sendMessageLabel: 'Invia messaggio',
    systemNoticeLabel: 'Avviso',
    sourcesLabel: 'Fonti',
    typingLabel: 'UltreIA sta scrivendo',
  },
  fr: {
    inputPlaceholder: 'Écrivez votre question ici',
    blockedInputPlaceholder: 'Vous pourrez continuer plus tard',
    inputAriaLabel: 'Écrivez votre question pour UltreIA',
    minimizeChatLabel: 'Réduire le chat',
    resetChatLabel: 'Redémarrer',
    sendMessageLabel: 'Envoyer le message',
    systemNoticeLabel: 'Avis',
    sourcesLabel: 'Sources',
    typingLabel: 'UltreIA est en train d’écrire',
  },
  de: {
    inputPlaceholder: 'Schreibe hier deine Frage',
    blockedInputPlaceholder: 'Du kannst später fortfahren',
    inputAriaLabel: 'Schreibe deine Frage an UltreIA',
    minimizeChatLabel: 'Chat minimieren',
    resetChatLabel: 'Neu starten',
    sendMessageLabel: 'Nachricht senden',
    systemNoticeLabel: 'Hinweis',
    sourcesLabel: 'Quellen',
    typingLabel: 'UltreIA schreibt',
  },
};

@Component({
  selector: 'ultreia-chat-widget',
  imports: [CommonModule, FormsModule, ChatMessageFormatPipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  readonly botName = environment.botName;
  readonly botStatusLabel = environment.botStatusLabel;
  private _welcomeMessage = environment.welcomeMessage;
  @HostBinding('class.is-embedded-frame')
  readonly isEmbeddedFrame = window.self !== window.top;
  readonly isOpen = signal(false);
  readonly draftMessage = signal('');
  readonly connectionState = signal<ChatConnectionState>('disconnected');
  readonly errorMessage = signal('');
  readonly isBotTyping = signal(false);
  readonly isConversationBlocked = signal(false);
  readonly chatLanguage = signal<ChatLanguage>(DEFAULT_CHAT_LANGUAGE);
  readonly messages = signal<WidgetMessage[]>([]);
  readonly messageMaxCharacters = signal(DEFAULT_MESSAGE_MAX_CHARACTERS);
  readonly draftMessageLength = computed(() => this.draftMessage().length);
  readonly uiText = computed(() => UI_TRANSLATIONS[this.chatLanguage()]);
  readonly canSendMessage = computed(
    () =>
      Boolean(this.draftMessage().trim()) &&
      this.connectionState() === 'connected' &&
      !this.isBotTyping() &&
      !this.isConversationBlocked() &&
      this.draftMessageLength() <= this.messageMaxCharacters(),
  );
  @ViewChild('chatBody')
  private _chatBody?: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput')
  private _messageInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChildren('messageRow')
  private _messageRows?: QueryList<ElementRef<HTMLElement>>;

  private _destroyRef = inject(DestroyRef);
  private _chatConfigService = inject(ChatConfigService);
  private _chatHistoryService = inject(ChatHistoryService);
  private _chatSessionService = inject(ChatSessionService);
  private _chatSocketService = inject(ChatSocketService);

  constructor() {
    this._chatSocketService.connectionState$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((state) => {
        this.connectionState.set(state);
      });

    this._chatSocketService.messages$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((payload) => {
        const botMessage = this._createBotMessage(payload);

        this.isBotTyping.set(false);
        this._setChatLanguage(payload.lang);
        this._appendMessage(botMessage);
        this._scheduleScrollToLatestMessageStart();
      });

    this._chatSocketService.errors$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((error) => {
        this.isBotTyping.set(false);
        this._handleChatError(error);
      });

    this._hydrateConversation();
    this._loadChatConfig();
  }

  toggleWidget(): void {
    const nextState = !this.isOpen();

    this.isOpen.set(nextState);
    this.errorMessage.set('');
    this._notifyFrameState(nextState);

    if (nextState) {
      this._hydrateConversation();
      this._chatSocketService.connect();
      this._scheduleScrollToBottom();
      this._scheduleMessageInputResize();
      return;
    }

    this._chatSocketService.disconnect();
    this.isBotTyping.set(false);
  }

  updateDraftMessage(value: string): void {
    this.draftMessage.set(value.slice(0, this.messageMaxCharacters()));
    this._scheduleMessageInputResize();
  }

  resetConversation(): void {
    const previousSessionId = this._chatSessionService.getSessionId();

    this._chatSocketService.resetConnection();
    this._clearHistory(previousSessionId);
    this._startNewConversation(this._chatSessionService.resetSession());
    this.draftMessage.set('');
    this.errorMessage.set('');
    this.isBotTyping.set(false);
    this.isConversationBlocked.set(false);
    this._scheduleMessageInputResize();

    if (this.isOpen()) {
      this._chatSocketService.connect();
      this._scheduleScrollToBottom();
    }
  }

  sendMessage(): void {
    const message = this.draftMessage().trim();

    if (!message || !this.canSendMessage()) {
      return;
    }

    const sessionId = this._chatSessionService.getOrCreateSessionId();
    const userMessage = this._createMessage('user', message);

    this._appendMessage(userMessage);
    this._scheduleScrollToBottom();

    this._chatSocketService.sendMessage({
      message,
      sessionId,
    });

    this.draftMessage.set('');
    this.errorMessage.set('');
    this.isBotTyping.set(true);
    this._scheduleMessageInputResize();
    this._scheduleScrollToBottom();
  }

  handleEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.shiftKey) {
      return;
    }

    keyboardEvent.preventDefault();
    this.sendMessage();
  }

  get connectionLabel(): string {
    switch (this.connectionState()) {
      case 'connected':
        return this.botStatusLabel;
      case 'connecting':
        return 'Conectando...';
      case 'error':
        return 'Sin conexión';
      default:
        return 'Desconectado';
    }
  }

  trackByMessageId(_index: number, message: WidgetMessage): string {
    return message.id;
  }

  hasStructuredSources(message: WidgetMessage): boolean {
    return Boolean(message.sources?.length);
  }

  getSourcePreviewSources(message: WidgetMessage): ChatSource[] {
    return message.sources?.slice(0, 3) ?? [];
  }

  formatTime(value: string): string {
    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? ''
      : new Intl.DateTimeFormat('es-ES', {
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
  }

  resizeMessageInput(): void {
    this._scheduleMessageInputResize();
  }

  private _toIsoDate(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
  }

  private _hydrateConversation(): void {
    const sessionId = this._chatSessionService.getOrCreateSessionId();
    const blockState = this._chatHistoryService.getConversationBlockState(sessionId);
    const storedLanguage = this._chatHistoryService.getLanguage(sessionId);

    if (blockState.hasExpired) {
      this._restartExpiredBlockedConversation(sessionId);
      return;
    }

    const storedMessages = this._chatHistoryService.getHistory(sessionId);

    this.isConversationBlocked.set(blockState.isBlocked);
    this.isBotTyping.set(false);
    this._setChatLanguage(storedLanguage);

    if (blockState.isBlocked) {
      this.errorMessage.set(blockState.blockedMessage || OFF_TOPIC_BLOCKED_MESSAGE);
    }

    if (storedMessages.length) {
      this.messages.set(storedMessages);
      return;
    }

    this._startNewConversation(sessionId);
  }

  private _getWelcomeMessages(): WidgetMessage[] {
    const welcomeMessage = this._welcomeMessage.trim();

    if (!welcomeMessage) {
      return [];
    }

    return [this._createMessage('bot', welcomeMessage)];
  }

  private _loadChatConfig(): void {
    this._chatConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const initialMessage = config.initialMessage.trim();

        this._welcomeMessage = initialMessage;
        this.messageMaxCharacters.set(config.messageMaxCharacters);
        this.updateDraftMessage(this.draftMessage());
        this._refreshInitialMessage();
      });
  }

  private _refreshInitialMessage(): void {
    const currentMessages = this.messages();

    if (this._hasUserMessages(currentMessages) || currentMessages.length > 1) {
      return;
    }

    const welcomeMessages = this._getWelcomeMessages();

    if (currentMessages[0]?.content === welcomeMessages[0]?.content) {
      return;
    }

    const sessionId = this._chatSessionService.getOrCreateSessionId();
    this.messages.set(welcomeMessages);
    this._saveMessages(sessionId, welcomeMessages);
    this._scheduleScrollToBottom();
  }

  private _appendMessage(message: WidgetMessage): void {
    const sessionId = this._chatSessionService.getOrCreateSessionId();
    const messages = [...this.messages(), message];

    this.messages.set(messages);
    this._saveMessages(sessionId, messages);
  }

  private _startNewConversation(sessionId: string): void {
    const welcomeMessages = this._getWelcomeMessages();

    this.chatLanguage.set(DEFAULT_CHAT_LANGUAGE);
    this.isConversationBlocked.set(false);
    this.isBotTyping.set(false);
    this.messages.set(welcomeMessages);
    this._saveMessages(sessionId, welcomeMessages);
    this._chatHistoryService.setConversationBlocked(sessionId, false, null, null);
  }

  private _restartExpiredBlockedConversation(expiredSessionId: string): void {
    this._chatSocketService.resetConnection();
    this._chatHistoryService.clearHistory(expiredSessionId);
    this._chatSessionService.clearSession();
    this._startNewConversation(this._chatSessionService.getOrCreateSessionId());
    this.draftMessage.set('');
    this.errorMessage.set('');
    this.isBotTyping.set(false);
    this._scheduleMessageInputResize();

    if (this.isOpen()) {
      this._chatSocketService.connect();
      this._scheduleScrollToBottom();
    }
  }

  private _saveMessages(sessionId: string, messages: WidgetMessage[]): void {
    if (messages.length) {
      this._chatHistoryService.saveHistory(sessionId, messages);
      return;
    }

    this._chatHistoryService.clearHistory(sessionId);
  }

  private _clearHistory(sessionId: string | null): void {
    if (!sessionId) {
      return;
    }

    this._chatHistoryService.clearHistory(sessionId);
  }

  private _createMessage(
    author: WidgetMessage['author'],
    content: string,
    createdAt = new Date().toISOString(),
  ): WidgetMessage {
    return {
      id: crypto.randomUUID(),
      author,
      content,
      createdAt,
    };
  }

  private _createBotMessage(payload: ReceiveMessagePayload): WidgetMessage {
    const message = this._createMessage(
      'bot',
      payload.message,
      this._toIsoDate(payload.receivedAt),
    );
    const sources = this._normalizeSources(payload.sources);

    return sources.length ? { ...message, sources } : message;
  }

  private _normalizeSources(sources: ReceiveMessagePayload['sources']): ChatSource[] {
    if (!Array.isArray(sources)) {
      return [];
    }

    return sources
      .map((source) => this._normalizeSource(source))
      .filter((source): source is ChatSource => Boolean(source));
  }

  private _normalizeSource(source: ChatSourcePayload): ChatSource | null {
    const url = this._getSourceUrl(source);

    if (!url) {
      return null;
    }

    return {
      url,
      label: this._getSourceLabel(source) || url,
      iconUrl: this._getSourceIconUrl(source, url),
    };
  }

  private _getSourceUrl(source: ChatSourcePayload): string | null {
    return this._getSafeUrl(source.url || source.href);
  }

  private _getSourceLabel(source: ChatSourcePayload): string {
    return (source.label || source.title || '').trim();
  }

  private _getSourceIconUrl(source: ChatSourcePayload, sourceUrl: string): string | undefined {
    const explicitIconUrl = this._getSafeUrl(source.iconUrl || source.faviconUrl);

    if (explicitIconUrl) {
      return explicitIconUrl;
    }

    return this._isPilgrimSource(sourceUrl) ? PILGRIM_SOURCE_ICON_URL : undefined;
  }

  private _getSafeUrl(value: string | undefined): string | null {
    if (!value) {
      return null;
    }

    try {
      const url = new URL(value);

      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  private _isPilgrimSource(sourceUrl: string): boolean {
    try {
      return PILGRIM_HOST_PATTERN.test(new URL(sourceUrl).hostname);
    } catch {
      return false;
    }
  }

  private _hasUserMessages(messages: WidgetMessage[]): boolean {
    return messages.some((message) => message.author === 'user');
  }

  private _handleChatError(error: ChatErrorNotification): void {
    this.isBotTyping.set(false);

    if (error.code !== 'OFF_TOPIC_LIMIT_EXCEEDED') {
      this.errorMessage.set(error.message);
      return;
    }

    const sessionId = this._chatSessionService.getOrCreateSessionId();
    const blockedUntil = new Date(Date.now() + OFF_TOPIC_BLOCK_DURATION_MS).toISOString();

    this.isConversationBlocked.set(true);
    this.errorMessage.set(error.message || OFF_TOPIC_BLOCKED_MESSAGE);
    this._chatHistoryService.setConversationBlocked(
      sessionId,
      true,
      blockedUntil,
      this.errorMessage(),
    );
    this._chatSocketService.disconnect();
    this._scheduleScrollToBottom();
    this._scheduleMessageInputResize();
  }

  private _setChatLanguage(lang: unknown): void {
    if (!this._isSupportedChatLanguage(lang)) {
      return;
    }

    const sessionId = this._chatSessionService.getOrCreateSessionId();

    this.chatLanguage.set(lang);
    this._chatHistoryService.setLanguage(sessionId, lang);
  }

  private _isSupportedChatLanguage(value: unknown): value is ChatLanguage {
    return typeof value === 'string' && SUPPORTED_CHAT_LANGUAGES.has(value as ChatLanguage);
  }

  private _notifyFrameState(isOpen: boolean): void {
    if (window.parent === window) {
      return;
    }

    window.parent.postMessage(
      {
        type: WIDGET_FRAME_MESSAGE_TYPE,
        isOpen,
      },
      '*',
    );
  }

  private _scheduleScrollToBottom(): void {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this._scrollToBottom();
      });
    });
  }

  private _scheduleScrollToLatestMessageStart(): void {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this._scrollToLatestMessageStart();
      });
    });
  }

  private _scheduleMessageInputResize(): void {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this._resizeMessageInput();
      });
    });
  }

  private _resizeMessageInput(): void {
    const element = this._messageInput?.nativeElement;

    if (!element) {
      return;
    }

    element.style.height = 'auto';
    const maxHeight = this._getMessageInputMaxHeight();
    const height = Math.min(element.scrollHeight, maxHeight);
    element.style.height = `${height}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private _getMessageInputMaxHeight(): number {
    return window.innerWidth <= MOBILE_VIEWPORT_MAX_WIDTH
      ? MOBILE_MESSAGE_INPUT_MAX_HEIGHT
      : MESSAGE_INPUT_MAX_HEIGHT;
  }

  private _scrollToBottom(): void {
    const element = this._chatBody?.nativeElement;

    if (!element) {
      return;
    }

    element.scrollTop = element.scrollHeight;
  }

  private _scrollToLatestMessageStart(): void {
    const chatBody = this._chatBody?.nativeElement;
    const latestMessage = this._messageRows?.last?.nativeElement;

    if (!chatBody || !latestMessage) {
      return;
    }

    const chatBodyTop = chatBody.getBoundingClientRect().top;
    const messageTop = latestMessage.getBoundingClientRect().top;
    const nextScrollTop =
      chatBody.scrollTop + messageTop - chatBodyTop - MESSAGE_SCROLL_TOP_OFFSET;

    chatBody.scrollTop = Math.max(0, nextScrollTop);
  }
}
